import React, { useEffect, useState, memo } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import { ArrowLeftIcon, ArrowRightIcon, XIcon, CheckIcon } from 'lucide-react';
export const ReviewSystem = ({
  classes,
  activeClass,
  activeNote,
  onEndReview
}) => {
  const [stage, setStage] = useState('intro'); // intro, memorize, recall, feedback
  const [section, setSection] = useState(null);
  const [userResponse, setUserResponse] = useState('');
  const [feedback, setFeedback] = useState(null);
  // Find active class and note
  const classObj = classes.find(c => c.id === activeClass);
  const noteObj = classObj?.notes.find(n => n.id === activeNote);
  // Prepare review sections by splitting note content
  useEffect(() => {
    if (noteObj?.content) {
      // For demo, we'll just use the whole note as one section
      setSection({
        id: 1,
        content: noteObj.content
      });
    }
  }, [noteObj]);
  const handleContinue = () => {
    if (stage === 'intro') {
      setStage('memorize');
    } else if (stage === 'memorize') {
      setStage('recall');
    } else if (stage === 'recall') {
      // Simple comparison for demo - in a real app this would use NLP
      const accuracy = calculateAccuracy(section.content, userResponse);
      setFeedback({
        accuracy,
        missed: accuracy < 100 ? 'Some key points were missed.' : ''
      });
      setStage('feedback');
    } else {
      // Reset and end review
      onEndReview();
    }
  };
  // Simple accuracy calculation for demo purposes
  const calculateAccuracy = (original, response) => {
    if (!original || !response) return 0;
    const originalWords = new Set(original.toLowerCase().split(/\s+/));
    const responseWords = new Set(response.toLowerCase().split(/\s+/));
    let matchCount = 0;
    responseWords.forEach(word => {
      if (originalWords.has(word)) matchCount++;
    });
    return Math.min(100, Math.round(matchCount / originalWords.size * 100));
  };
  if (!section) {
    return <div className="flex items-center justify-center h-full" data-id="element-83">
        <div className="text-center" data-id="element-84">
          <h2 className="text-xl font-medium mb-2" data-id="element-85">No content to review</h2>
          <button onClick={onEndReview} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg" data-id="element-86">
            Exit Review
          </button>
        </div>
      </div>;
  }
  return <div className="h-full flex flex-col" data-id="element-87">
      <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center" data-id="element-88">
        <div data-id="element-89">
          <Breadcrumbs className={classObj?.name} note={noteObj?.title} data-id="element-90" />
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-91">
            Review: {noteObj?.title}
          </h1>
        </div>
        <button onClick={onEndReview} className="text-gray-500 hover:text-gray-700" data-id="element-92">
          <XIcon size={24} data-id="element-93" />
        </button>
      </div>
      <div className="flex-1 overflow-auto p-4" data-id="element-94">
        <div className="bg-white rounded-lg shadow p-6 mb-4" data-id="element-95">
          {stage === 'intro' && <div className="text-center py-12" data-id="element-96">
              <h2 className="text-xl font-bold mb-4" data-id="element-97">Ready to Review</h2>
              <p className="mb-8 text-gray-600" data-id="element-98">
                You will be shown a section of your notes to memorize. Then
                you'll be asked to recall as much as you can from memory.
              </p>
              <button onClick={handleContinue} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-lg" data-id="element-99">
                Start Review
              </button>
            </div>}
          {stage === 'memorize' && <div data-id="element-100">
              <h2 className="text-xl font-bold mb-4" data-id="element-101">Memorize this section</h2>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6" data-id="element-102">
                <p className="whitespace-pre-line" data-id="element-103">{section.content}</p>
              </div>
              <div className="text-center" data-id="element-104">
                <p className="mb-4 text-gray-600" data-id="element-105">
                  Take your time to memorize the content above.
                </p>
                <button onClick={handleContinue} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg" data-id="element-106">
                  I'm Ready to Recall
                </button>
              </div>
            </div>}
          {stage === 'recall' && <div data-id="element-107">
              <h2 className="text-xl font-bold mb-4" data-id="element-108">Recall from Memory</h2>
              <p className="mb-4 text-gray-600" data-id="element-109">
                Write down as much as you can remember from the section you just
                studied.
              </p>
              <textarea className="w-full h-48 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4" placeholder="Type everything you remember here..." value={userResponse} onChange={e => setUserResponse(e.target.value)} data-id="element-110" />
              <div className="text-center" data-id="element-111">
                <button onClick={handleContinue} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg" data-id="element-112">
                  Check My Recall
                </button>
              </div>
            </div>}
          {stage === 'feedback' && feedback && <div data-id="element-113">
              <h2 className="text-xl font-bold mb-4" data-id="element-114">Feedback</h2>
              <div className="mb-6" data-id="element-115">
                <div className="flex items-center mb-2" data-id="element-116">
                  <span className="text-lg font-medium mr-2" data-id="element-117">Accuracy:</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-6" data-id="element-118">
                    <div className={`h-6 rounded-full ${feedback.accuracy > 70 ? 'bg-green-500' : feedback.accuracy > 40 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
                  width: `${feedback.accuracy}%`
                }} data-id="element-119"></div>
                  </div>
                  <span className="ml-2 font-bold" data-id="element-120">{feedback.accuracy}%</span>
                </div>
                {feedback.missed && <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4" data-id="element-121">
                    <div className="flex" data-id="element-122">
                      <div className="flex-shrink-0" data-id="element-123">
                        <span className="text-yellow-400" data-id="element-124">⚠️</span>
                      </div>
                      <div className="ml-3" data-id="element-125">
                        <p className="text-sm text-yellow-700" data-id="element-126">
                          {feedback.missed}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>
              <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-127">
                <h3 className="font-medium mb-2" data-id="element-128">Original Content:</h3>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4" data-id="element-129">
                  <p data-id="element-130">{section.content}</p>
                </div>
                <h3 className="font-medium mb-2" data-id="element-131">Your Response:</h3>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200" data-id="element-132">
                  <p data-id="element-133">{userResponse}</p>
                </div>
              </div>
              <div className="flex justify-between mt-8" data-id="element-134">
                <button onClick={onEndReview} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-135">
                  <XIcon size={18} className="mr-1" data-id="element-136" />
                  Exit Review
                </button>
                <button onClick={handleContinue} className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg" data-id="element-137">
                  Continue
                  <ArrowRightIcon size={18} className="ml-1" data-id="element-138" />
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};